import { useLookbookListingContext } from '@app/components/lookbook/context/LookbookListingContext';
import { Copy, Remove, Settings } from '@app/components/various/Icons';
import { addToast } from '@app/components/various/Toasts';
import { compiledPaths } from '@app/paths';
import { useDeleteLookbook } from '@app/services/hooks/lookbook/useDeleteLookbook';
import { copyToClipboard } from '@app/utils/clipboard';
import { isDefined } from '@app/utils/is';
import { IconComponentProps } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useConfirmationGuard } from '../useConfirmationGuard';

const RemoveIcon = (props: IconComponentProps) => <Remove {...props} className="text-error-45" />;

interface Props {
  lookbookId: string | undefined;
}

export const useLookbookOptions = ({ lookbookId }: Props) => {
  const { t } = useTranslation(['common', 'confirmationConfig', 'lookbook']);
  const confirmationGuard = useConfirmationGuard();
  const { mutate: deleteLookbook } = useDeleteLookbook();
  const { setIsSettingsModalVisible } = useLookbookListingContext();

  const handleCopyLinkClick = React.useCallback(() => {
    if (!isDefined(lookbookId)) {
      return;
    }

    const link = `${window.origin}${compiledPaths.LOOKBOOK_SINGLE({ lookbookId })}`;

    copyToClipboard(link);
    addToast(t('common:link_copied_message'));
  }, [lookbookId, t]);

  const handleSettingsClick = () => {
    setIsSettingsModalVisible(true);
  };

  const deleteAction = confirmationGuard(() => ({
    onOk: () => isDefined(lookbookId) && deleteLookbook(lookbookId),
    ...t('confirmationConfig:delete_lookbook', { returnObjects: true }),
  }));

  return React.useMemo(
    () => [
      {
        icon: Copy,
        key: 'copy-link',
        label: t('lookbook:copy_link'),
        onClick: handleCopyLinkClick,
      },
      {
        icon: Settings,
        key: 'settings',
        label: t('lookbook:navigation.add_pages'),
        onClick: handleSettingsClick,
      },
      {
        icon: RemoveIcon,
        key: 'delete',
        label: t('common:delete'),
        onClick: deleteAction,
      },
    ],
    [handleCopyLinkClick, t],
  );
};
